<template>
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-md-4 left">
          <div class="title">
            {{ $t('sidebar.pos') }} > {{ $t('sidebar.possale') }}
          </div>
        </div>
        <div class="col-md-8 right">
          <div class="action">
            <base-dropdown-filter-download
              :items="DOWNLOAD_EXTS"
              :loading="false"
              @onSelect="handleSelectDownload"
            />
            <base-filter-data-type
              :type="dataType"
              :items="REPORT_DATA_TYPES"
              @onSelect="handleSelectDataType"
            />
            <base-dropdown-filter-date-range
              :type="dateType"
              :range="{ start: dateStart, end: dateEnd }"
              @onSubmit="handleSubmitFilter"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">

      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <div
        v-else
        class="col-md-12 no-padding mt-2">
        <template v-if="posSales.length">
          <section class="mt-2 mb-4">
            <div>
              <section-title
                :title="datePeriod(dateStart, dateEnd)"
                fontweight="bold"/>
            </div>
          </section>
          <keep-alive>
            <report-sale-table
              v-if="dataType === REPORT_DATA_TYPE_VALUE.TABLE"
              :items="posSales"
            />
            <report-sale-chart
              v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_BAR"
              :items="posSales"
              :selectedLabels.sync="chartSelectedLabels"
              type="bar"
            />
            <report-sale-chart
              v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_LINE"
              :items="posSales"
              :selectedLabels.sync="chartSelectedLabels"
              type="line"
            />
          </keep-alive>
        </template>
        <template v-else>
          {{ $t('message.nodatafound') }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { FILTER_DATE_TYPE, DOWNLOAD_EXTS, DOWNLOAD_EXT_VALUE, REPORT_DATA_TYPES, REPORT_DATA_TYPE_VALUE } from '../../../../helpers/enum'
import { getDateStart, getDateEnd, getLastSevenDay } from '../../../../helpers'
import sidebarModule from './../../sidebarModule'
import XLSX from 'xlsx'
import { getFileNamePosSale } from '@/helpers/exportFormater'

const LAST_SEVEN_DAY = getLastSevenDay()

export default {
  data () {
    return {
      REPORT_DATA_TYPES,
      REPORT_DATA_TYPE_VALUE,
      DOWNLOAD_EXTS,
      ready: false,
      dateType: FILTER_DATE_TYPE.DAY,
      dateStart: LAST_SEVEN_DAY.START,
      dateEnd: LAST_SEVEN_DAY.END,
      dataType: REPORT_DATA_TYPE_VALUE.TABLE,
      chartSelectedLabels: {},
    }
  },

  computed: {
    ...mapGetters({
      dateformat: 'getDateFormat',
    }),
    posSales () {
      return this.$store.state.report.posSales.map((item) => {
        return {
          ...item,
          date: (() => {
            if (this.dateType === FILTER_DATE_TYPE.DAY) {
              return this.getMyLocalDate(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.MONTH) {
              return this.getMyLocalMonthYear(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.YEAR) {
              return this.getMyLocalYear(item.date, { short: false })
            }
            return item.date
          })(),
        }
      })
    },
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.reports.title'),
      current: {
        app_name: 'reports',
        app_id: 8,
        module_id: 2,
        submodule_id: 1,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 1,
      submenuAtIdx: 0,
    })
  },

  created () {
    this.fetchData()
  },

  destroyed: function () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.ready = false
      await this.$store.dispatch('getPosSales', {
        'filter[date_start]': getDateStart(this.dateStart),
        'filter[date_end]': getDateEnd(this.dateEnd),
        'filter[date_type]': this.dateType,
        'timezone': moment.tz.guess(),
      })
      this.ready = true
      this.showFinish(this.$Progress)
    },
    handleSubmitFilter ({ type, start, end }) {
      this.dateType = type
      this.dateStart = start
      this.dateEnd = end
      this.fetchData()
    },
    handleSelectDataType ({ value = null }) {
      this.dataType = value
    },
    handleSelectDownload ({ value = null }) {
      if (value === DOWNLOAD_EXT_VALUE.EXCEL) {
        this.exportAs('xlsx')
      } else if (value === DOWNLOAD_EXT_VALUE.CSV) {
        this.exportAs('csv')
      }
    },
    async exportAs (fileType) {
      /* convert state to workbook */
      const headColumns = [
        this.$t('message.date'),
        this.$t('message.cashier'),
        this.$t('apps.pos.message.possession'),
        this.$t('message.transactions'),
        this.$t('message.product'),
        this.getRoleMetadata.view.fields.cost ? this.$t('message.cost') : '{hiden}',
        this.getRoleMetadata.view.fields.discount ? this.$t('message.discount') : '{hiden}',
        this.getRoleMetadata.view.fields.tax ? this.$t('message.tax') : '{hiden}',
        this.getRoleMetadata.view.fields.collect ? this.$t('message.collect') : '{hiden}',
        this.getRoleMetadata.view.fields.refund ? this.$t('message.refund') : '{hiden}',
        this.getRoleMetadata.view.fields.net ? this.$t('message.net') : '{hiden}',
        this.getRoleMetadata.view.fields.profit ? this.$t('message.profit') : '{hiden}',
      ].filter((v) => v !== '{hiden}')
      const bodyRows = this.$store.state.report.posSales.map((item) => (
        [
          item.date,
          item.cashiersCount,
          item.sessionsCount,
          item.transactionsCount,
          item.productsCount,
          this.getRoleMetadata.view.fields.cost ? item.cost : '{hiden}',
          this.getRoleMetadata.view.fields.discount ? item.discount : '{hiden}',
          this.getRoleMetadata.view.fields.tax ? item.tax : '{hiden}',
          this.getRoleMetadata.view.fields.collect ? item.collect : '{hiden}',
          this.getRoleMetadata.view.fields.refund ? item.refund : '{hiden}',
          this.getRoleMetadata.view.fields.net ? item.net : '{hiden}',
          this.getRoleMetadata.view.fields.profit ? item.profit : '{hiden}',
        ].filter((v) => v != '{hiden}')
      ))
      const data = [headColumns, ...bodyRows]
      const ws = XLSX.utils.aoa_to_sheet(data)
      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet')

      /* generate file and send to client */
      const fileName = getFileNamePosSale(this.$t, fileType)

      XLSX.writeFile(wb, fileName)
    },
  },
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/apps/report/pos/Sale.vue